import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Image,
  Film,
  FileAudio,
  FileText,
  FileSpreadsheet,
  File,
  Download,
  ChevronLeft,
  ChevronRight,
  MapPin,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

export interface ViewerFile {
  url: string;
  mimeType: string;
  filename: string;
  description?: string | null;
  animals?: Array<{ animalId: string; animalName: string | null }>;
  locations?: string[];
}

interface FileViewerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  files: ViewerFile[];
  initialIndex?: number;
}

export function getFileTypeIcon(mimeType: string | null): LucideIcon {
  if (!mimeType) return File;
  if (mimeType.startsWith("image/")) return Image;
  if (mimeType.startsWith("video/")) return Film;
  if (mimeType.startsWith("audio/")) return FileAudio;
  if (mimeType.includes("pdf")) return FileText;
  if (mimeType.includes("spreadsheet") || mimeType.includes("csv") || mimeType.includes("excel")) return FileSpreadsheet;
  if (mimeType.startsWith("text/")) return FileText;
  return File;
}

function isPreviewable(mimeType: string): boolean {
  return (
    mimeType.startsWith("image/") ||
    mimeType.startsWith("video/") ||
    mimeType.startsWith("audio/") ||
    mimeType.includes("pdf")
  );
}

export { isPreviewable };

function FileContent({ file }: { file: ViewerFile }) {
  const { url, mimeType, filename } = file;

  if (mimeType.startsWith("image/")) {
    return (
      <div className="flex items-center justify-center w-full h-full min-h-[300px] max-h-[70vh]">
        <img
          src={url}
          alt={filename}
          className="max-w-full max-h-[70vh] object-contain rounded-md"
          data-testid="viewer-image"
        />
      </div>
    );
  }

  if (mimeType.startsWith("video/")) {
    return (
      <div className="flex items-center justify-center w-full" data-testid="viewer-video">
        <video
          controls
          autoPlay
          className="max-w-full max-h-[70vh] rounded-md"
          style={{ width: "100%" }}
        >
          <source src={url} type={mimeType} />
        </video>
      </div>
    );
  }

  if (mimeType.startsWith("audio/")) {
    return (
      <div className="flex items-center justify-center w-full py-8" data-testid="viewer-audio">
        <audio controls className="w-full">
          <source src={url} type={mimeType} />
        </audio>
      </div>
    );
  }

  if (mimeType.includes("pdf")) {
    return (
      <iframe
        src={url}
        title={filename}
        className="w-full h-[70vh] rounded-md border"
        data-testid="viewer-pdf"
      />
    );
  }

  const IconComponent = getFileTypeIcon(mimeType);
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4" data-testid="viewer-fallback">
      <IconComponent className="h-16 w-16 text-muted-foreground/50" />
      <p className="text-sm text-muted-foreground">{filename}</p>
      <p className="text-xs text-muted-foreground">This file type cannot be previewed</p>
      <Button variant="outline" size="sm" asChild>
        <a href={url} download={filename} target="_blank" rel="noopener noreferrer">
          <Download className="h-4 w-4 mr-2" />
          Download
        </a>
      </Button>
    </div>
  );
}

export function FileViewerDialog({ open, onOpenChange, files, initialIndex = 0 }: FileViewerDialogProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const hasMultiple = files.length > 1;

  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
    }
  }, [open, initialIndex]);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => Math.min(prev + 1, files.length - 1));
  }, [files.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  useEffect(() => {
    if (!open || !hasMultiple) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, hasMultiple, goNext, goPrev]);

  if (files.length === 0) return null;

  const currentFile = files[currentIndex] ?? files[0];
  const IconComponent = getFileTypeIcon(currentFile.mimeType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl w-[95vw] max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden"
        data-testid="file-viewer-dialog"
      >
        <DialogHeader className="flex flex-row items-center justify-between gap-2 px-4 py-3 border-b shrink-0">
          <div className="flex flex-col min-w-0 flex-1 gap-0.5">
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              <IconComponent className="h-4 w-4 text-muted-foreground shrink-0" />
              <DialogTitle className="text-sm font-medium truncate" data-testid="viewer-filename">
                {currentFile.filename}
              </DialogTitle>
              {hasMultiple && (
                <span className="text-xs text-muted-foreground shrink-0" data-testid="viewer-position">
                  {currentIndex + 1} of {files.length}
                </span>
              )}
              {currentFile.locations && currentFile.locations.length > 0 && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground shrink-0" data-testid="viewer-location">
                  <MapPin className="h-3 w-3" />
                  {currentFile.locations.join(", ")}
                </span>
              )}
              {currentFile.animals && currentFile.animals.map(a => (
                <a key={a.animalId} href={`/animals/${a.animalId}`} target="_blank" rel="noopener noreferrer" className="shrink-0">
                  <Badge variant="outline" className="text-xs cursor-pointer" data-testid={`viewer-animal-${a.animalId}`}>
                    {a.animalName || a.animalId}
                  </Badge>
                </a>
              ))}
            </div>
            {currentFile.description && (
              <p className="text-xs text-muted-foreground truncate pl-6" data-testid="viewer-description">
                {currentFile.description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0 mr-6">
            {hasMultiple && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goPrev}
                  disabled={currentIndex === 0}
                  data-testid="viewer-prev"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={goNext}
                  disabled={currentIndex === files.length - 1}
                  data-testid="viewer-next"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            )}
            <Button variant="ghost" size="icon" asChild data-testid="viewer-download">
              <a href={currentFile.url} download={currentFile.filename} target="_blank" rel="noopener noreferrer">
                <Download className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </DialogHeader>
        <div className="flex-1 overflow-auto p-4">
          <FileContent file={currentFile} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
